import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';

const CanvasSeatMap = ({ 
  seatMapData, 
  selectedSeats = [], 
  toggleSeat = () => {}, 
  stageText = "STAGE",
  width = 800,
  height = 600,
  rowOrder = []
}) => {
  const canvasRef = useRef(null);
  const [hoveredSeat, setHoveredSeat] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, content: '' });
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastPanPoint, setLastPanPoint] = useState({ x: 0, y: 0 });

  // Detect layout type
  const layoutType = useMemo(() => {
    if (!seatMapData) return 'normal';
    
    // Check if seatMapData is an object with type field (round layout - includes both round and rectangular tables)
    if (typeof seatMapData === 'object' && !Array.isArray(seatMapData)) {
      if (seatMapData.type === 'round' || (seatMapData.sections && seatMapData.sections.length > 0 && 
           (seatMapData.sections[0]?.tableType === 'round' || seatMapData.sections[0]?.tableType === 'rectangular'))) {
        return 'round';
      }
      return 'normal';
    }
    
    // Check if it's an array with first element having type field or sections with round/rectangular tables
    if (Array.isArray(seatMapData) && seatMapData.length > 0) {
      const firstItem = seatMapData[0];
      if (typeof firstItem === 'object') {
        if (firstItem.type === 'round' || (firstItem.sections && firstItem.sections.length > 0 && 
            (firstItem.sections[0]?.tableType === 'round' || firstItem.sections[0]?.tableType === 'rectangular'))) {
          return 'round';
        }
        // Check if it's a seat object with tableName (indicating round layout seat)
        if (firstItem.tableName) {
          return 'round';
        }
      }
    }
    
    return 'normal';
  }, [seatMapData]);

  // Process seat data into drawable format
  const processedData = useMemo(() => {
    if (!seatMapData) return null;

    // Handle round layout
    if (layoutType === 'round') {
      // Get the layout object (could be direct object or array with one object)
      const layoutObj = Array.isArray(seatMapData) ? seatMapData[0] : seatMapData;
      if (!layoutObj || !layoutObj.sections) return null;

      const seatSize = 30; // Size for circular seats
      const tableRadius = 50; // Radius for round tables
      const tableWidth = 120; // Width for rectangular tables
      const tableHeight = 60; // Height for rectangular tables
      const padding = 40;
      const stageHeight = layoutObj.stagePosition ? 60 : 0;
      const stagePosition = layoutObj.stagePosition || null;

      // Extract all seats and tables
      const drawableSeats = [];
      const tables = [];

      layoutObj.sections.forEach(section => {
        if (section.tableType === 'round' || section.tableType === 'rectangular') {
          // Calculate table dimensions for rectangular tables based on seat positions
          let tableWidthCalc = tableWidth;
          let tableHeightCalc = tableHeight;
          
          if (section.tableType === 'rectangular' && section.rows && section.rows.length > 0) {
            const allSeats = section.rows[0].seats || [];
            if (allSeats.length > 0) {
              // Calculate bounding box of seats to determine table size
              const seatXs = allSeats.map(s => s.x);
              const seatYs = allSeats.map(s => s.y);
              const minSeatX = Math.min(...seatXs);
              const maxSeatX = Math.max(...seatXs);
              const minSeatY = Math.min(...seatYs);
              const maxSeatY = Math.max(...seatYs);
              
              // Table should be slightly smaller than the bounding box of seats
              const seatSpreadX = maxSeatX - minSeatX;
              const seatSpreadY = maxSeatY - minSeatY;
              tableWidthCalc = Math.max(80, seatSpreadX * 0.6);
              tableHeightCalc = Math.max(40, seatSpreadY * 0.6);
            }
          }

          // Store table info
          if (section.tableType === 'round') {
            tables.push({
              id: section._id || section.id,
              // name: section.name,
               name: section.name.split(' ')[0], 
              x: section.x,
              y: section.y,
              color: section.color || '#8B4513',
              type: 'round',
              radius: tableRadius
            });
          } else if (section.tableType === 'rectangular') {
            tables.push({
              id: section._id || section.id,
              name: section.name,
              x: section.x,
              y: section.y,
              color: section.color || '#8B4513',
              type: 'rectangular',
              width: tableWidthCalc,
              height: tableHeightCalc
            });
          }

          // Extract seats from this table
          section.rows.forEach(row => {
            row.seats.forEach(seat => {
              drawableSeats.push({
                ...seat,
                id: seat.id || seat._id,
                x: seat.x,
                y: seat.y,
                width: seatSize,
                height: seatSize,
                radius: seatSize / 2,
                tableName: section.name,
                tableId: section._id || section.id,
                tableType: section.tableType,
                status: seat.reserved ? 'booked' : 'available',
                reserved: seat.reserved || false,
                price: seat.price || 0,
                childprice: seat.childprice || null,
                label: seat.label || seat.seatNumber || '',
                color: seat.color || '#96CEB4',
                angle: seat.angle || 0
              });
            });
          });
        }
      });

      // Calculate bounding box
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      
      drawableSeats.forEach(seat => {
        minX = Math.min(minX, seat.x - seat.radius);
        minY = Math.min(minY, seat.y - seat.radius);
        maxX = Math.max(maxX, seat.x + seat.radius);
        maxY = Math.max(maxY, seat.y + seat.radius);
      });

      tables.forEach(table => {
        if (table.type === 'round') {
          minX = Math.min(minX, table.x - table.radius);
          minY = Math.min(minY, table.y - table.radius);
          maxX = Math.max(maxX, table.x + table.radius);
          maxY = Math.max(maxY, table.y + table.radius);
        } else if (table.type === 'rectangular') {
          minX = Math.min(minX, table.x - table.width / 2);
          minY = Math.min(minY, table.y - table.height / 2);
          maxX = Math.max(maxX, table.x + table.width / 2);
          maxY = Math.max(maxY, table.y + table.height / 2);
        }
      });

      // Ensure we have valid bounds
      if (minX === Infinity) {
        minX = 0;
        minY = 0;
        maxX = width;
        maxY = height;
      }

      const totalWidth = Math.max(maxX - minX + padding * 2, width);
      const totalHeight = Math.max(maxY - minY + stageHeight + padding * 2, height);

      return {
        layoutType: 'round',
        seats: drawableSeats,
        tables,
        layout: {
          totalWidth,
          totalHeight,
          seatSize,
          tableRadius,
          tableWidth,
          tableHeight,
          stageHeight,
          stagePosition,
          padding,
          minX,
          minY
        }
      };
    }

    // Handle normal layout (existing logic)
    if (!Array.isArray(seatMapData) || seatMapData.length === 0) return null;

    // Group seats by row while preserving original order
    const groupedSeats = {};
    const rowOrder = [];

    // First pass: collect unique rows in their original order
    seatMapData.forEach(seat => {
      if (!groupedSeats[seat.row]) {
        groupedSeats[seat.row] = [];
        rowOrder.push(seat.row);
      }
      groupedSeats[seat.row].push(seat);
    });

    // Use rowOrder from the data instead of sorting
    const sortedRows = rowOrder;

    // Calculate layout
    const seatSize = 30;
    const seatGap = 5;
    const rowGap = 15;
    const rowLabelWidth = 100;
    const stageHeight = 60;
    const padding = 40;

    // Calculate max width needed
    let maxSeatsInRow = 0;
    sortedRows.forEach(row => {
      const totalItems = groupedSeats[row].length;
      maxSeatsInRow = Math.max(maxSeatsInRow, totalItems);
    });

    const totalWidth = rowLabelWidth + (maxSeatsInRow * (seatSize + seatGap)) + padding * 2;
    const totalHeight = stageHeight + (sortedRows.length * (seatSize + rowGap)) + padding * 2;

    // Create drawable seats with positions
    const drawableSeats = [];
    sortedRows.forEach((row, rowIndex) => {
      const seats = groupedSeats[row];
      const rowY = stageHeight + padding + (rowIndex * (seatSize + rowGap));
      
      // Calculate row offset for alignment
      const actualSeats = seats.filter(s => s.type !== 'space');
      const seatCount = actualSeats.length;
      let rowOffset = 0;
      
      // If this row has more seats than typical (26), shift left by 2 columns
      if (seatCount > 26) {
        rowOffset = -2 * (seatSize + seatGap); // 2 columns to the left
      }
      
      let currentX = padding + rowLabelWidth + rowOffset;
      
      seats.forEach((seat, seatIndex) => {
        if (seat.type === 'space') {
          currentX += seatSize + seatGap;
          return;
        }

        drawableSeats.push({
          ...seat,
          x: currentX,
          y: rowY,
          width: seatSize,
          height: seatSize,
          rowIndex,
          seatIndex
        });

        currentX += seatSize + seatGap;
      });
    });

    return {
      layoutType: 'normal',
      seats: drawableSeats,
      rows: sortedRows,
      groupedSeats,
      layout: {
        totalWidth,
        totalHeight,
        seatSize,
        seatGap,
        rowGap,
        rowLabelWidth,
        stageHeight,
        padding
      }
    };
  }, [seatMapData, layoutType, width, height]);

  // Color schemes for different sections
  const sectionColors = {
    VIP: { available: '#28a745ad', selected: '#007bff', booked: '#dc3545', hover: '#d91a72' },
    Premium: { available: '#28a745ad', selected: '#007bff', booked: '#dc3545', hover: '#5a32a3' },
    Balcony: { available: '#28a745ad', selected: '#007bff', booked: '#dc3545', hover: '#138496' },
    Economy: { available: '#28a745ad', selected: '#007bff', booked: '#dc3545', hover: '#e8650e' },
    default: { available: '#28a745ad', selected: '#007bff', booked: '#dc3545', hover: '#218838' }
  };

  // Draw function
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !processedData) return;

    const ctx = canvas.getContext('2d');
    const { seats, layout, layoutType } = processedData;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Apply transformations
    ctx.save();
    ctx.translate(offset.x, offset.y);
    ctx.scale(scale, scale);

    if (layoutType === 'round') {
      // Draw round layout
      const { tables } = processedData;
      const { stageHeight, stagePosition, padding } = layout;

      // Draw stage if exists
      if (stageHeight > 0 && stagePosition) {
        const stageX = stagePosition.x || layout.totalWidth / 2;
        const stageY = stagePosition.y || padding;
        const stageWidth = 300; // Default stage width
        const stageHeightDraw = stageHeight - 20;
        
        ctx.fillStyle = '#6c757d';
        ctx.fillRect(
          stageX - stageWidth / 2,
          stageY,
          stageWidth,
          stageHeightDraw
        );
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          stageText,
          stageX,
          stageY + stageHeightDraw / 2 + 6
        );
      } else if (stageHeight > 0) {
        // Fallback to top center if no position specified
        ctx.fillStyle = '#6c757d';
        ctx.fillRect(
          padding,
          padding,
          layout.totalWidth - padding * 2,
          stageHeight - 20
        );
        
        ctx.fillStyle = 'white';
        ctx.font = 'bold 16px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          stageText,
          layout.totalWidth / 2,
          padding + (stageHeight - 20) / 2 + 6
        );
      }

      // Draw tables first (so seats appear on top)
      tables.forEach(table => {
        if (table.type === 'round') {
          // Draw round table circle (coordinates are already absolute)
          ctx.fillStyle = table.color;
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(table.x, table.y, table.radius, 0, Math.PI * 2);
          ctx.fill();
          ctx.stroke();

          // Draw table label
          ctx.fillStyle = 'white';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(
            table.name,
            table.x,
            table.y + 5
          );
        } else if (table.type === 'rectangular') {
          // Draw rectangular table (coordinates are already absolute)
          ctx.fillStyle = table.color;
          ctx.strokeStyle = '#000000';
          ctx.lineWidth = 2;
          ctx.fillRect(
            table.x - table.width / 2,
            table.y - table.height / 2,
            table.width,
            table.height
          );
          ctx.strokeRect(
            table.x - table.width / 2,
            table.y - table.height / 2,
            table.width,
            table.height
          );

          // Draw table label
          ctx.fillStyle = 'white';
          ctx.font = 'bold 14px Arial';
          ctx.textAlign = 'center';
          ctx.fillText(
            table.name,
            table.x,
            table.y + 5
          );
        }
      });

      // Draw seats
      seats.forEach(seat => {
        const colors = sectionColors[seat.section] || sectionColors.default;
        // const colors =sectionColors.default;
        const isSelected = selectedSeats.includes(seat.id);
        const isHovered = hoveredSeat && hoveredSeat.id === seat.id;

        const baseAvailableColor = seat.color || colors.available;

        let fillColor, strokeColor;

        // Check seat status
        const seatStatus =
          seat.reserved ? 'booked'
          : seat.status === 'booked' ? 'booked'
          : seat.status === 'available' ? 'available'
          : 'available';
 fillColor = colors.available;
          strokeColor = colors.available;
        // if (seatStatus === 'booked') {
        //   fillColor = colors.booked;
        //   strokeColor = colors.booked;
        // } else if (isSelected) {
        //   fillColor = colors.selected;
        //   strokeColor = colors.selected;
        // } else if (isHovered && seatStatus === 'available') {
        //   fillColor = baseAvailableColor;
        //   strokeColor = baseAvailableColor;
        // } else {
        //   fillColor = seatStatus === 'available' ? baseAvailableColor : '#f8f9fa';
        //   strokeColor = seatStatus === 'available' ? baseAvailableColor : colors.available;
        // }

        // Draw circular seat (coordinates are already absolute)
        ctx.fillStyle = fillColor;
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(seat.x, seat.y, seat.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();

        // Draw seat label
        ctx.fillStyle = 'white';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          seat.label || seat.number || seat.seatNumber || '',
          seat.x,
          seat.y + 4
        );
      });
    } else {
      // Draw normal layout (existing logic)
      const { rows } = processedData;

      // Draw stage
      ctx.fillStyle = '#6c757d';
      ctx.fillRect(
        layout.padding,
        layout.padding,
        layout.totalWidth - layout.padding * 2,
        layout.stageHeight - 20
      );
      
      ctx.fillStyle = 'white';
      ctx.font = 'bold 16px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(
        stageText,
        layout.totalWidth / 2,
        layout.padding + (layout.stageHeight - 20) / 2 + 6
      );

      // Draw row labels
      ctx.fillStyle = '#495057';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      
      rows.forEach((row, rowIndex) => {
        const rowY = layout.stageHeight + layout.padding + (rowIndex * (layout.seatSize + layout.rowGap));
        
        // Calculate row offset for alignment (same logic as seat positioning)
        const rowSeats = processedData.groupedSeats[row];
        const actualSeats = rowSeats.filter(s => s.type !== 'space');
        const seatCount = actualSeats.length;
        let rowOffset = 0;
        
        // If this row has more seats than typical (26), shift left by 2 columns
        if (seatCount > 26) {
          rowOffset = -2 * (layout.seatSize + layout.seatGap);
        }
        
        const labelX = layout.padding + layout.rowLabelWidth / 2 + rowOffset;
        
        ctx.fillText(
          row,
          labelX,
          rowY + layout.seatSize / 2 + 5
        );
      });

      // Draw seats
      seats.forEach(seat => {
        const colors = sectionColors[seat.section] || sectionColors.default;
        const isSelected = selectedSeats.includes(seat.id);
        const isHovered = hoveredSeat && hoveredSeat.id === seat.id;

        const baseAvailableColor = seat.color || colors.available;

        let fillColor, strokeColor;

        // FIXED: Check for seat status properly (also support 'reserved' boolean from API)
        const seatStatus =
          seat.reserved ? 'booked'
          : seat.status === 'booked' ? 'booked'
          : seat.status === 'available' ? 'available'
          : seat.status;

        if (seatStatus === 'booked') {
          fillColor = colors.booked;
          strokeColor = colors.booked;
        } else if (isSelected) {
          fillColor = colors.selected;
          strokeColor = colors.selected;
        } else if (isHovered && seatStatus === 'available') {
          fillColor = baseAvailableColor;
          strokeColor = baseAvailableColor;
        } else {
          fillColor = seatStatus === 'available' ? baseAvailableColor : '#f8f9fa';
          strokeColor = seatStatus === 'available' ? baseAvailableColor : colors.available;
        }

        // Draw seat background
        ctx.fillStyle = fillColor;
        ctx.strokeStyle = strokeColor;
        ctx.lineWidth = 2;

        ctx.beginPath();
        ctx.roundRect(seat.x, seat.y, seat.width, seat.height, 8);
        ctx.fill();
        ctx.stroke();

        // Draw seat number
        ctx.fillStyle = 'white';
        ctx.font = 'bold 11px Arial';
        ctx.textAlign = 'center';
        ctx.fillText(
          seat.label || seat.number || seat.seatNumber,
          seat.x + seat.width / 2,
          seat.y + seat.height / 2 + 4
        );
      });
    }

    ctx.restore();
  }, [processedData, selectedSeats, hoveredSeat, scale, offset, stageText]);

  // Handle mouse and touch events
  const getMousePos = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };
    const rect = canvas.getBoundingClientRect();
    
    // Handle touch events separately for accurate coordinate mapping
    if (e.touches && e.touches.length > 0) {
      const touch = e.touches[0];
      // Convert touch screen coordinates to canvas coordinates
      const canvasX = touch.clientX - rect.left;
      const canvasY = touch.clientY - rect.top;
      // Apply pan offset and scale transformation
      return {
        x: (canvasX - offset.x) / scale,
        y: (canvasY - offset.y) / scale
      };
    }
    
    // Handle mouse events (desktop)
    const clientX = e.clientX || 0;
    const clientY = e.clientY || 0;
    const canvasX = clientX - rect.left;
    const canvasY = clientY - rect.top;
    return {
      x: (canvasX - offset.x) / scale,
      y: (canvasY - offset.y) / scale
    };
  };

  const getTouchPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    return {
      x: (touch.clientX - rect.left - offset.x) / scale,
      y: (touch.clientY - rect.top - offset.y) / scale
    };
  };

  const getSeatAtPosition = (x, y) => {
    if (!processedData) return null;
    
    if (processedData.layoutType === 'round') {
      // For round layout, check circular collision (coordinates are already absolute)
      return processedData.seats.find(seat => {
        const dx = x - seat.x;
        const dy = y - seat.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        return distance <= seat.radius;
      });
    } else {
      // For normal layout, check rectangular collision
      return processedData.seats.find(seat => 
        x >= seat.x && x <= seat.x + seat.width &&
        y >= seat.y && y <= seat.y + seat.height
      );
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : lastPanPoint.x);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : lastPanPoint.y);
      
      const deltaX = clientX - lastPanPoint.x;
      const deltaY = clientY - lastPanPoint.y;
      
      setOffset(prev => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY
      }));
      
      setLastPanPoint({ x: clientX, y: clientY });
    } else {
      const mousePos = getMousePos(e);
      const seat = getSeatAtPosition(mousePos.x, mousePos.y);
      
      setHoveredSeat(seat);
      
      if (seat && seat.status === 'available') {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
        const priceText = seat.childprice !== undefined && seat.childprice !== null
          ? `Adult: £${seat.price} | Child: £${seat.childprice}`
          : `£${seat.price}`;
        
        // Format tooltip based on layout type
        let tooltipContent;
        if (processedData.layoutType === 'round') {
          tooltipContent = `${seat.tableName || 'Table'} - ${seat.label || 'Seat'} - ${priceText}`;
        } else {
          tooltipContent = `Row ${seat.row || seat.rowNumber} - Seat ${seat.number || seat.seatNumber} - ${priceText}`;
        }
        
        setTooltip({
          show: true,
          x: clientX - rect.left,
          y: clientY - rect.top,
          content: tooltipContent
        });
        canvas.style.cursor = 'pointer';
      } else if (seat && seat.status === 'booked') {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
        const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
        setTooltip({
          show: true,
          x: clientX - rect.left,
          y: clientY - rect.top,
          content: 'Already booked'
        });
        canvas.style.cursor = 'not-allowed';
      } else {
        setTooltip({ show: false, x: 0, y: 0, content: '' });
        canvasRef.current.style.cursor = isDragging ? 'grabbing' : 'grab';
      }
    }
  };

  const handleMouseDown = (e) => {
    const mousePos = getMousePos(e);
    const seat = getSeatAtPosition(mousePos.x, mousePos.y);
    
    // Check seat status - for round layout, use reserved field or status
    const seatStatus = seat?.reserved ? 'booked' : (seat?.status || 'available');
    if (seat && seatStatus === 'available') {
      toggleSeat(seat.id);
    } else {
      setIsDragging(true);
      const clientX = e.clientX || (e.touches && e.touches[0] ? e.touches[0].clientX : 0);
      const clientY = e.clientY || (e.touches && e.touches[0] ? e.touches[0].clientY : 0);
      setLastPanPoint({ x: clientX, y: clientY });
      canvasRef.current.style.cursor = 'grabbing';
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    if (canvasRef.current) {
      canvasRef.current.style.cursor = 'grab';
    }
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const mousePos = getMousePos(e);
    const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.5, Math.min(3, scale * zoomFactor));
    
    // Zoom towards mouse position
    const newOffset = {
      x: offset.x - (mousePos.x * (newScale - scale)),
      y: offset.y - (mousePos.y * (newScale - scale))
    };
    
    setScale(newScale);
    setOffset(newOffset);
  };

  // Handle touch zoom (pinch gesture)
  const [initialDistance, setInitialDistance] = useState(null);
  const [initialScale, setInitialScale] = useState(1);

  const getDistance = (touches) => {
    if (touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      // Two finger touch - prepare for pinch zoom
      setInitialDistance(getDistance(e.touches));
      setInitialScale(scale);
      e.preventDefault(); // Prevent scrolling during pinch zoom
    } else if (e.touches.length === 1) {
      // Single finger touch - check if it's on a seat first
      const touch = e.touches[0];
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        const canvasX = touch.clientX - rect.left;
        const canvasY = touch.clientY - rect.top;
        const mousePos = {
          x: (canvasX - offset.x) / scale,
          y: (canvasY - offset.y) / scale
        };
        const seat = getSeatAtPosition(mousePos.x, mousePos.y);
        
        // Check seat status properly
        const seatStatus = seat?.reserved ? 'booked' : (seat?.status || 'available');
        if (seat && seatStatus === 'available') {
          // It's a seat tap - prevent default and handle it
          e.preventDefault();
          toggleSeat(seat.id);
        } else {
          // Not a seat - prepare for dragging
          setIsDragging(true);
          setLastPanPoint({ x: touch.clientX, y: touch.clientY });
        }
      }
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2) {
      // Two finger touch - handle pinch zoom
      e.preventDefault(); // Prevent default when zooming
      const currentDistance = getDistance(e.touches);
      if (initialDistance && initialDistance > 0) {
        const scaleFactor = currentDistance / initialDistance;
        const newScale = Math.max(0.5, Math.min(3, initialScale * scaleFactor));
        setScale(newScale);
      }
    } else if (e.touches.length === 1) {
      if (isDragging) {
        // Actively dragging/panning canvas
        e.preventDefault();
        handleMouseMove(e);
      }
      // If not dragging, allow default scroll behavior
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length === 0) {
      // All touches ended
      setInitialDistance(null);
      setInitialScale(1);
      handleMouseUp();
    }
  };

  // Reset view
  const resetView = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  // Auto-fit view
  const fitToView = () => {
    if (!processedData) return;
    
    const canvas = canvasRef.current;
    const padding = 20;
    
    const scaleX = (canvas.width - padding * 2) / processedData.layout.totalWidth;
    const scaleY = (canvas.height - padding * 2) / processedData.layout.totalHeight;
    const newScale = Math.min(scaleX, scaleY, 1);
    
    const newOffset = {
      x: (canvas.width - processedData.layout.totalWidth * newScale) / 2,
      y: (canvas.height - processedData.layout.totalHeight * newScale) / 2
    };
    
    setScale(newScale);
    setOffset(newOffset);
  };

  // Effects
  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    canvas.addEventListener('mousemove', handleMouseMove);
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mouseup', handleMouseUp);
    canvas.addEventListener('mouseleave', handleMouseUp);
    canvas.addEventListener('wheel', handleWheel);

    // Touch events for mobile
    canvas.addEventListener('touchstart', handleTouchStart, { passive: true });
    canvas.addEventListener('touchmove', handleTouchMove, { passive: false }); // Keep false to allow preventDefault when needed
    canvas.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mouseup', handleMouseUp);
      canvas.removeEventListener('mouseleave', handleMouseUp);
      canvas.removeEventListener('wheel', handleWheel);
      
      // Remove touch event listeners
      canvas.removeEventListener('touchstart', handleTouchStart);
      canvas.removeEventListener('touchmove', handleTouchMove);
      canvas.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, lastPanPoint, scale, offset, processedData, selectedSeats, initialDistance, initialScale, toggleSeat, getSeatAtPosition]);

  // Auto-fit on data change
  useEffect(() => {
    if (processedData) {
      setTimeout(fitToView, 100);
    }
  }, [processedData]);

  return (
    <div style={{
      position: 'relative',
      background: '#f8f9fa',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
    }}>
      <div style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        zIndex: 10,
        display: 'flex',
        gap: '8px'
      }}>
        <button 
          onClick={resetView}
          style={{
            background: 'rgba(255, 255, 255, 0.9)',
            border: '1px solid #dee2e6',
            borderRadius: '6px',
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '12px',
            fontWeight: '500',
            transition: 'all 0.2s ease',
            backdropFilter: 'blur(4px)'
          }}
        >
          Reset View
        </button>
      </div>

      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          display: 'block',
          cursor: 'grab',
          touchAction: 'pan-y pinch-zoom' // Allow vertical scroll and pinch zoom, handle seat selection manually
        }}
        className='seat-map-canvas'
      />

      {tooltip.show && (
        <div 
          style={{ 
            position: 'absolute',
            left: tooltip.x, 
            top: tooltip.y,
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            pointerEvents: 'none',
            zIndex: 100,
            whiteSpace: 'nowrap',
            transform: 'translate(-50%, -100%)',
            marginTop: '-8px'
          }}
        >
          {tooltip.content}
          <div style={{
            content: '',
            position: 'absolute',
            top: '100%',
            left: '50%',
            marginLeft: '-4px',
            border: '4px solid transparent',
            borderTopColor: 'rgba(0, 0, 0, 0.8)'
          }} />
        </div>
      )}

      <div style={{
        position: 'absolute',
        bottom: '10px',
        left: '10px',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '12px',
        borderRadius: '8px',
        fontSize: '12px',
        backdropFilter: 'blur(4px)',
        border: '1px solid #dee2e6'
      }}>
        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#28a745' }}></div>
          <span>Available</span>
        </div> */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#007bff' }}></div>
          <span>Selected</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '16px', height: '16px', borderRadius: '4px', border: '1px solid #ccc', backgroundColor: '#dc3545' }}></div>
          <span>Booked</span>
        </div>
      </div>

      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        background: 'rgba(255, 255, 255, 0.9)',
        padding: '8px 12px',
        borderRadius: '6px',
        fontSize: '11px',
        color: '#666',
        backdropFilter: 'blur(4px)',
        border: '1px solid #dee2e6',
        maxWidth: '200px',
        textAlign: 'right'
      }}>
        Tap to select seats<br />
        Pinch to zoom • Drag on empty space to pan
      </div>
    </div>
  );
};


export default CanvasSeatMap;